import { FC, useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useDispatch } from "react-redux";
import {
  useGetBillingSalesOfficeQuery,
  useGetInvoiceTemplatesQuery,
  useGetMarketsQuery,
  useGetMdmOfficeById,
  useGetSearchRegionMutation,
} from "../../../../api/services/mdmOffice/mdmOfficeApis";
import {
  useGetCurrencies,
  useTaxTypes,
} from "../../../../api/services/program/programApis";
import {
  FormController,
  MuiBox,
  MuiCheckbox,
  MuiGrid,
  MuiMultiSelect,
  MuiSelect,
  MuiTextField,
} from "../../../../components";
import {
  MAX_LENGTH_75,
  MIN_LENGTH_3,
  PAGE_IDS,
} from "../../../../helpers/constants";
import {
  isNotEmpty,
  setApiDataToDropDownOption,
} from "../../../../helpers/utils";
import { useGridContainer } from "../../../../hooks/useGridContainer";
import { useMultiSelectARIALabels } from "../../../../hooks/useMultiSelectARIALabels";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { useToast } from "../../../../hooks/useToast";
import { toggleLoader } from "../../../../redux/slices/appState/appStateSlice";
import { MdmCreateOfficeFormKeys } from "../../modelsAndSchemas/models";
import { mdmCreateOfficeSiteCoreKeys } from "../../siteCore/SiteCoreKeys";

const pageId = PAGE_IDS.MDM_OFFICE_DETAILS;

interface OfficeDetailsProps {
  defaultTaxRef: React.RefObject<{ resetValues: (values?: string[]) => void }>;
  isEdit?: boolean;
  onBillingOfficeCheckChange: (isBillingOffice: boolean) => void;
}

export const OfficeDetails: FC<OfficeDetailsProps> = ({
  defaultTaxRef,
  isEdit,
  onBillingOfficeCheckChange,
}) => {
  const {
    useFetchLabel,
    commonLabels,
    fetchDropdownValues,
    commonValidationMessages,
  } = useSiteCore();
  const { control, getValues, setValue, formState, clearErrors } =
    useFormContext();
  const dispatch = useDispatch();
  const { displayToast } = useToast();
  const { commonARIALabel } = useNavigationSiteCore();
  const [isOfficeCodeDisabled, setIsOfficeCodeDisabled] = useState(false);

  const multiSelectARIALabels = useMultiSelectARIALabels();

  const { gridSizeMedium: gridSize, gridSizeMediumClass } =
    useGridContainer(true);

  const { defaultTax } = getValues();
  const {
    data: taxList,
    isLoading: isLoadingDefaultTax,
    isSuccess: isSuccessDefaultTax,
    isError: isErrorDefaultTax,
  } = useTaxTypes();

  const {
    data: currencyList,
    isLoading: isLoadingCurrencies,
    isError: isErrorCurrencies,
  } = useGetCurrencies();

  const {
    data: marketData,
    isLoading: isLoadingMarkets,
    isError: isErrorMarkets,
  } = useGetMarketsQuery();

  const {
    mutate: getSearchRegion,
    data: searchRegionData,
    isPending: isPendingSearchRegion,
    isError: isErrorSearchRegion,
  } = useGetSearchRegionMutation();

  const {
    data: invoiceTemplateData,
    isLoading: isLoadingInvoiceTemplate,
    isError: isErrorInvoiceTemplate,
  } = useGetInvoiceTemplatesQuery("Invoice");

  const {
    data: salesCreditTemplateData,
    isLoading: isLoadingSalesCreditTemplate,
    isError: isErrorSalesCreditTemplate,
  } = useGetInvoiceTemplatesQuery("SalesCredit");

  const {
    data: billingSalesOfficeData,
    isLoading: isLoadingBillingSalesOffice,
    isError: isErrorBillingSalesOffice,
  } = useGetBillingSalesOfficeQuery("Billing");

  const {
    data: parentOfficeData,
    isLoading: isLoadingParentOffice,
    isError: isErrorParentOffice,
  } = useGetBillingSalesOfficeQuery();

  const {
    data: salesCreditOfficeData,
    isLoading: isLoadingSalesCreditOffice,
    isError: isErrorSalesCreditOffice,
  } = useGetBillingSalesOfficeQuery("SalesCredit");

  const {
    data: officeData,
    isError: isOfficeError,
    isPending: isOfficeDataPending,
    mutate: fetchMdmOfficeById,
  } = useGetMdmOfficeById();

  const isSalesCreditOffice = useWatch({
    control,
    name: MdmCreateOfficeFormKeys.isSalesCreditOffice,
  });

  const isBillingOffice = useWatch({
    control,
    name: MdmCreateOfficeFormKeys.isBillingOffice,
  });

  useEffect(() => {
    if (isEdit && defaultTax && defaultTaxRef?.current) {
      defaultTaxRef.current?.resetValues(defaultTax);
    }
  }, [isEdit, defaultTax, isSuccessDefaultTax]);

  useEffect(() => {
    if (
      isErrorDefaultTax ||
      isErrorMarkets ||
      isErrorSearchRegion ||
      isErrorInvoiceTemplate ||
      isErrorSalesCreditTemplate ||
      isErrorBillingSalesOffice ||
      isErrorParentOffice ||
      isErrorSalesCreditOffice ||
      isErrorCurrencies ||
      isOfficeError
    ) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
    }
  }, [
    isErrorDefaultTax,
    isErrorMarkets,
    isErrorSearchRegion,
    isErrorInvoiceTemplate,
    isErrorSalesCreditTemplate,
    isErrorBillingSalesOffice,
    isErrorParentOffice,
    isErrorSalesCreditOffice,
    isErrorCurrencies,
    isOfficeError,
  ]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isLoadingOfficeData: {
          isVisible:
            isLoadingMarkets ||
            isLoadingInvoiceTemplate ||
            isLoadingSalesCreditTemplate ||
            isLoadingDefaultTax ||
            isLoadingBillingSalesOffice ||
            isLoadingSalesCreditOffice ||
            isPendingSearchRegion ||
            isLoadingParentOffice ||
            isLoadingCurrencies ||
            isOfficeDataPending,
          message: commonLabels.loadingPleaseWait,
        },
      }),
    );
    return () => {
      dispatch(toggleLoader({ isLoadingOfficeData: { isVisible: false } }));
    };
  }, [
    isLoadingMarkets,
    isLoadingInvoiceTemplate,
    isLoadingSalesCreditTemplate,
    isLoadingDefaultTax,
    isLoadingBillingSalesOffice,
    isLoadingSalesCreditOffice,
    isPendingSearchRegion,
    isLoadingParentOffice,
    isLoadingCurrencies,
    isOfficeDataPending,
  ]);

  const {
    createNewOfficeTitle,
    officeName,
    parentOffice,
    officeCode,
    officeType,
    market,
    searchRegion,
    billingOffice,
    salesCreditOffice,
    currency,
    invoiceTemplate,
    salesCreditTemplate,
    defaultTax: defaultTaxKey,
    status,
    createOfficeStatusList,
    OfficeTypeList,
  } = mdmCreateOfficeSiteCoreKeys;

  const getLabel = useFetchLabel(createNewOfficeTitle);

  const getDropdownValues = (key: string) => {
    return fetchDropdownValues(createNewOfficeTitle, key);
  };

  useEffect(() => {
    if (isEdit) {
      const marketId = getValues(MdmCreateOfficeFormKeys.marketId);
      handleMarketChange(marketId);

      const parentOfficeId = getValues(MdmCreateOfficeFormKeys.parentOfficeId);
      if (parentOfficeId) {
        setIsOfficeCodeDisabled(true);
      }
    }
  }, [isEdit]);

  const handleMarketChange = (marketId: string) => {
    if (marketId && isNotEmpty(marketId)) {
      getSearchRegion(marketId);
    }
  };

  const handleParentOfficeChange = (parentOfficeId: string) => {
    if (isNotEmpty(parentOfficeId)) {
      fetchMdmOfficeById(parentOfficeId);
      setIsOfficeCodeDisabled(true);
      if (formState.errors?.officeCode)
        clearErrors(MdmCreateOfficeFormKeys.officeCode);
    } else {
      setIsOfficeCodeDisabled(false);
      setValue(MdmCreateOfficeFormKeys.officeCode, "");
    }
  };

  useEffect(() => {
    if (isNotEmpty(officeData)) {
      setValue(
        MdmCreateOfficeFormKeys.officeCode,
        officeData?.officeDetails?.officeCode,
      );
    }
  }, [officeData]);
  return (
    <MuiBox className="container-office-details">
      <MuiGrid container>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={MdmCreateOfficeFormKeys.officeName}
          >
            <MuiTextField
              label={getLabel(officeName)}
              id={`${pageId}-${MdmCreateOfficeFormKeys.officeName}`}
              placeholder={commonLabels.inputPlaceHolder}
              showCloseIcon
              maxLength={MAX_LENGTH_75}
              isMandatory
              requiredARIALabel={commonARIALabel.required}
              tooltipMessage={
                commonValidationMessages.fieldSpecialCharactersTooltipMessage
              }
              tooltipHeaderTitle={
                commonValidationMessages.allowedSpecialCharactersTitle
              }
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={MdmCreateOfficeFormKeys.parentOfficeId}
          >
            <MuiSelect
              label={getLabel(parentOffice)}
              options={setApiDataToDropDownOption(parentOfficeData)}
              id={`${pageId}-${MdmCreateOfficeFormKeys.parentOfficeId}`}
              requiredARIALabel={commonARIALabel.required}
              visibleDefaultSelectOption
              onChange={(event) =>
                handleParentOfficeChange(event.target.value as string)
              }
              placeholder={commonLabels.selectPlaceHolder}
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={MdmCreateOfficeFormKeys.officeCode}
          >
            <MuiTextField
              label={getLabel(officeCode)}
              id={`${pageId}-${MdmCreateOfficeFormKeys.officeCode}`}
              placeholder={commonLabels.inputPlaceHolder}
              showCloseIcon
              maxLength={MIN_LENGTH_3}
              isMandatory
              disabled={isOfficeCodeDisabled}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={MdmCreateOfficeFormKeys.officeType}
          >
            <MuiSelect
              label={getLabel(officeType)}
              options={getDropdownValues(OfficeTypeList)}
              id={`${pageId}-${MdmCreateOfficeFormKeys.officeType}`}
              isMandatory
              requiredARIALabel={commonARIALabel.required}
              placeholder={commonLabels.selectPlaceHolder}
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={MdmCreateOfficeFormKeys.marketId}
          >
            <MuiSelect
              label={getLabel(market)}
              options={setApiDataToDropDownOption(marketData)}
              id={`${pageId}-${MdmCreateOfficeFormKeys.marketId}`}
              isMandatory
              onChange={(e) => handleMarketChange(e.target.value as string)}
              requiredARIALabel={commonARIALabel.required}
              placeholder={commonLabels.selectPlaceHolder}
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={MdmCreateOfficeFormKeys.regionId}
          >
            <MuiSelect
              label={getLabel(searchRegion)}
              options={setApiDataToDropDownOption(searchRegionData)}
              id={`${pageId}-${MdmCreateOfficeFormKeys.regionId}`}
              isMandatory
              requiredARIALabel={commonARIALabel.required}
              placeholder={commonLabels.selectPlaceHolder}
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={MdmCreateOfficeFormKeys.currencyId}
          >
            <MuiSelect
              label={getLabel(currency)}
              options={setApiDataToDropDownOption(currencyList || [])}
              id={`${pageId}-${MdmCreateOfficeFormKeys.currencyId}`}
              isMandatory
              requiredARIALabel={commonARIALabel.required}
              placeholder={commonLabels.selectPlaceHolder}
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={MdmCreateOfficeFormKeys.invoiceTemplateId}
          >
            <MuiSelect
              label={getLabel(invoiceTemplate)}
              options={setApiDataToDropDownOption(invoiceTemplateData)}
              id={`${pageId}-${MdmCreateOfficeFormKeys.invoiceTemplateId}`}
              requiredARIALabel={commonARIALabel.required}
              placeholder={commonLabels.selectPlaceHolder}
              visibleDefaultSelectOption
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={MdmCreateOfficeFormKeys.salesCreditTemplateId}
          >
            <MuiSelect
              label={getLabel(salesCreditTemplate)}
              options={setApiDataToDropDownOption(salesCreditTemplateData)}
              id={`${pageId}-${MdmCreateOfficeFormKeys.salesCreditTemplateId}`}
              requiredARIALabel={commonARIALabel.required}
              placeholder={commonLabels.selectPlaceHolder}
              visibleDefaultSelectOption
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={MdmCreateOfficeFormKeys.defaultTax}
            ref={defaultTaxRef}
          >
            <MuiMultiSelect
              label={getLabel(defaultTaxKey)}
              options={setApiDataToDropDownOption(taxList ?? [])}
              showSearch
              showChip
              id={`${pageId}-${MdmCreateOfficeFormKeys.defaultTax}`}
              chipId={`${pageId}-${MdmCreateOfficeFormKeys.defaultTax}-chip`}
              dialogTitle={"All default tax"}
              isDialogTitleWithEllipsis={false}
              selectPlaceHolder={commonLabels.selectPlaceHolder}
              searchPlaceHolder={commonLabels.search}
              selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
              doneText={commonLabels.done}
              allARIAProps={multiSelectARIALabels}
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={MdmCreateOfficeFormKeys.statusId}
          >
            <MuiSelect
              id={`${pageId}-${MdmCreateOfficeFormKeys.statusId}`}
              label={getLabel(status)}
              options={getDropdownValues(createOfficeStatusList)}
              visibleDefaultSelectOption={false}
              requiredARIALabel={commonARIALabel.required}
              placeholder={commonLabels.selectPlaceHolder}
            />
          </FormController>
        </MuiGrid>
      </MuiGrid>

      <MuiGrid container>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={MdmCreateOfficeFormKeys.isBillingOffice}
          >
            <MuiCheckbox
              onChange={(e) => onBillingOfficeCheckChange(e.target.checked)}
              id={`${pageId}-${MdmCreateOfficeFormKeys.isBillingOffice}`}
              checkboxLabel={getLabel(billingOffice)}
              checked={isBillingOffice}
            />
          </FormController>
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={MdmCreateOfficeFormKeys.isSalesCreditOffice}
          >
            <MuiCheckbox
              id={`${pageId}-${MdmCreateOfficeFormKeys.isSalesCreditOffice}`}
              checkboxLabel={getLabel(salesCreditOffice)}
              checked={isSalesCreditOffice}
            />
          </FormController>
        </MuiGrid>
      </MuiGrid>

      <MuiGrid container>
        {!isBillingOffice && (
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={MdmCreateOfficeFormKeys.billingOfficeId}
            >
              <MuiSelect
                label={getLabel(billingOffice)}
                options={setApiDataToDropDownOption(billingSalesOfficeData)}
                id={`${pageId}-${MdmCreateOfficeFormKeys.billingOfficeId}`}
                isMandatory
                requiredARIALabel={commonARIALabel.required}
                placeholder={commonLabels.selectPlaceHolder}
              />
            </FormController>
          </MuiGrid>
        )}

        {!isSalesCreditOffice && (
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={MdmCreateOfficeFormKeys.salesCreditOfficeId}
            >
              <MuiSelect
                label={getLabel(salesCreditOffice)}
                options={setApiDataToDropDownOption(salesCreditOfficeData)}
                id={`${pageId}-${MdmCreateOfficeFormKeys.salesCreditOfficeId}`}
                isMandatory
                requiredARIALabel={commonARIALabel.required}
                placeholder={commonLabels.selectPlaceHolder}
              />
            </FormController>
          </MuiGrid>
        )}
      </MuiGrid>
    </MuiBox>
  );
};
